import { useSelector } from 'react-redux';
import { paths } from '../../common/constants/pathVariables';
import { useSearchParams } from '../../common/hooks/useSearchParams';
import {
  additionalServicesMsg,
  barringServicesMsg,
  basicDetailsMsg,
  billingAccountAndUserMsg,
  numberSettingsMsg,
  ringServiceSettingsMsg,
  simCardMsg,
  t,
  timeSettingsMsg,
  vakioServiceSettings,
  voiceSubscriptionsMsg,
} from '../../common/i18n';
import { getContactDisplayValue } from '../../common/utils/billingAccountUtils';
import { formatPhoneNumber } from '../../common/utils/phoneNumberUtils';
import {
  getAddonAssociationsAndDependencies,
  getAvailableAddOnCodes,
  isSubscriptionInSfdc,
} from '../../common/utils/subscriptionUtils';
import { SubscriptionPbxDetails } from '../../generated/api/subscriptionPbxDetails';
import { SubscriptionType } from '../../generated/api/subscriptionType';
import { MobileSubscriptionLayout, getSubscriptionUrl } from './MobileSubscriptionLayout';
import type { CategorySubscriptionLayoutProps } from './MobileSubscriptionLayout';
import type { Contact } from '../../generated/api/contact';
import type { Subscription } from '../../generated/api/subscription';
import type { State } from '../../selfservice/common/store.js';
import type { LocalNaviItem } from '../LocalNavi';

const getTitle = (subscription: Subscription, contacts?: Contact[]) => {
  const selectedContact = contacts?.find(c => c.contactId === subscription?.subscriptionContactId);
  const selectedContactName = getContactDisplayValue(selectedContact) || undefined;
  return selectedContactName ?? subscription?.subscriptionPurposeOfUse ?? '…';
};

const getSubtitle = (subscription: Subscription, companyName?: string) => {
  if (
    subscription.subscriptionType !== SubscriptionType.SPECIAL_NUMBER &&
    subscription.details?.mobile?.pbxConfiguration &&
    subscription.subscriptionUserFriendlyId
  ) {
    const pbxTypeFromDetails = subscription.details.mobile.pbxConfiguration.pbxConfigurationDetails.pbxType;
    if (pbxTypeFromDetails && pbxTypeFromDetails.length > 0) {
      return (
        <>
          {formatPhoneNumber(subscription.subscriptionUserFriendlyId, true)}
          <span> | </span>
          {pbxTypeFromDetails === SubscriptionPbxDetails.PbxTypeEnum.VAKIO ? 'Vakio' : ''}
          {pbxTypeFromDetails === SubscriptionPbxDetails.PbxTypeEnum.RING ? 'Ring' : ''}{' '}
          {formatPhoneNumber(subscription.details.mobile.pbxConfiguration.corporateNumber, true)}
          <span> | </span>
          {companyName}
        </>
      );
    }
  }
  if (
    subscription.details?.mobilePbxEndUserService?.pbxConfigurationDetails.pbxType ===
      SubscriptionPbxDetails.PbxTypeEnum.RING &&
    subscription.details.mobilePbxEndUserService.corporateNumber
  ) {
    return `${formatPhoneNumber(subscription.details.mobilePbxEndUserService.corporateNumber, true)} | ${companyName}`;
  }
  return subscription.subscriptionUserFriendlyId
    ? `${subscription.subscriptionUserFriendlyId} | ${companyName}`
    : companyName;
};

const getPbxSolution = (subscription: Subscription, pbxSolutions: Subscription[]) =>
  subscription.details!.mobile &&
  subscription.details!.mobile.pbxConfiguration &&
  pbxSolutions.find(
    pbxSolution => pbxSolution.subscriptionId === subscription.details!.mobile!.pbxConfiguration!.pbxSolutionId
  );

const hasVakio = (subscription: Subscription, pbxSolutions: Subscription[]) => {
  const pbxSolution = getPbxSolution(subscription, pbxSolutions);
  const companyHasMobilePbxLite =
    pbxSolutions.filter(({ subscriptionType }) => subscriptionType === SubscriptionType.MOBILE_PBX_LITE).length > 0;
  return (
    companyHasMobilePbxLite &&
    pbxSolution &&
    subscription.details!.mobile!.pbxConfiguration!.pbxConfigurationDetails.pbxType ===
      SubscriptionPbxDetails.PbxTypeEnum.VAKIO
  );
};

const hasRing = (subscription: Subscription, pbxSolutions: Subscription[]) => {
  const pbxSolution = getPbxSolution(subscription, pbxSolutions);
  return (
    pbxSolution &&
    subscription.details!.mobile!.pbxConfiguration!.pbxConfigurationDetails.pbxType ===
      SubscriptionPbxDetails.PbxTypeEnum.RING
  );
};

const getNavigationCategories = (
  subscription: Subscription,
  pbxSolutions: Subscription[],
  mdmId: string,
  hideAdditionalContent: boolean,
  hideBarringServices: boolean,
  hideBillingAccountAndUser: boolean
) => {
  const navigationCategories = [
    {
      children: t.C9M1(basicDetailsMsg),
      to: getSubscriptionUrl(paths.PS_MOBILE_SUBSCRIPTION, subscription, mdmId),
    },
  ] as LocalNaviItem[];

  if (!hideBillingAccountAndUser) {
    navigationCategories.push({
      children: t.CZ63(billingAccountAndUserMsg),
      to: getSubscriptionUrl(paths.PS_MOBILE_SUBSCRIPTION_BA_CONTRACT_PARTY, subscription, mdmId),
    });
  }

  if (hideAdditionalContent) {
    return navigationCategories;
  }

  if (
    !hideBarringServices &&
    isSubscriptionInSfdc(subscription) &&
    [SubscriptionType.MOBILE, SubscriptionType.LANDLINE].includes(subscription.subscriptionType)
  ) {
    navigationCategories.push({
      children: t.ZUCA(barringServicesMsg),
      to: getSubscriptionUrl(paths.PS_MOBILE_SUBSCRIPTION_BARRING_SERVICES, subscription, mdmId),
    });
  }

  if (isSubscriptionInSfdc(subscription) && subscription.subscriptionType === SubscriptionType.SPECIAL_NUMBER) {
    navigationCategories.push({
      children: t.SC8P(timeSettingsMsg),
      to: getSubscriptionUrl(paths.PS_MOBILE_SUBSCRIPTION_TIME_SETTINGS, subscription, mdmId),
    });
    navigationCategories.push({
      children: t.AN17(numberSettingsMsg),
      to: getSubscriptionUrl(paths.PS_MOBILE_SUBSCRIPTION_NUMBER_SETTINGS, subscription, mdmId),
    });
  }

  if (
    isSubscriptionInSfdc(subscription) &&
    [SubscriptionType.MOBILE, SubscriptionType.LANDLINE, SubscriptionType.SPECIAL_NUMBER].includes(
      subscription.subscriptionType
    )
  ) {
    navigationCategories.push({
      children: t.LXSR(additionalServicesMsg),
      to: getSubscriptionUrl(paths.PS_MOBILE_SUBSCRIPTION_ADDONS, subscription, mdmId),
    });
  }

  if (subscription.subscriptionType === SubscriptionType.MOBILE) {
    navigationCategories.push({
      children: t.PIZC(simCardMsg),
      to: getSubscriptionUrl(paths.PS_MOBILE_SUBSCRIPTION_SIM_CARD, subscription, mdmId),
    });
  }

  if (
    isSubscriptionInSfdc(subscription) &&
    hasRing(subscription, pbxSolutions) &&
    [SubscriptionType.MOBILE, SubscriptionType.MOBILE_PBX_END_USER_SERVICE].includes(subscription.subscriptionType)
  ) {
    navigationCategories.push({
      children: t.ALD4(ringServiceSettingsMsg),
      to: getSubscriptionUrl(paths.PS_MOBILE_SUBSCRIPTION_RING, subscription, mdmId),
    });
  }

  if (
    isSubscriptionInSfdc(subscription) &&
    hasVakio(subscription, pbxSolutions) &&
    subscription.subscriptionType === SubscriptionType.MOBILE
  ) {
    navigationCategories.push({
      children: t.ZCXQ(vakioServiceSettings),
      to: getSubscriptionUrl(paths.PS_MOBILE_SUBSCRIPTION_VAKIO, subscription, mdmId),
    });
  }
  return navigationCategories;
};

export const VoiceSubscriptionLayout = ({
  addOnRules,
  addOnRulesMobilePbx,
  children,
  companyInfo,
  contacts,
  subscription,
}: CategorySubscriptionLayoutProps) => {
  const { mdmId } = useSearchParams<{ mdmId: string }>();
  if (!mdmId) {
    throw new Error('missing mdmId');
  }
  const { suspendSubscriptionInitiated, terminateSubscriptionInitiated } = useSelector((s: State) => ({
    suspendSubscriptionInitiated: s?.selfservice?.pendingSubscriptionActions?.suspendSubscriptionInitiated ?? false,
    terminateSubscriptionInitiated: s?.selfservice?.pendingSubscriptionActions?.terminateSubscriptionInitiated ?? false,
  }));
  const hideAdditionalContent =
    !subscription?.details || suspendSubscriptionInitiated || terminateSubscriptionInitiated;
  const hideBillingAccountAndUser = suspendSubscriptionInitiated || terminateSubscriptionInitiated;
  const { associations } = getAddonAssociationsAndDependencies(subscription, addOnRules, addOnRulesMobilePbx);
  const availableAddOnCodes = getAvailableAddOnCodes(associations, subscription.commercialProductId);
  const hideBarringServices = !availableAddOnCodes.length;

  return (
    <MobileSubscriptionLayout
      subscription={subscription}
      headingTop={subscription.subscriptionName}
      heading={getTitle(subscription, contacts)}
      headingBottom={getSubtitle(subscription, companyInfo?.companyName)}
      heroPicto="sim"
      navigationItems={getNavigationCategories(
        subscription,
        companyInfo?.pbxSolutions ?? [],
        mdmId,
        hideAdditionalContent,
        hideBarringServices,
        hideBillingAccountAndUser
      )}
      categoryBreadcrumbAndPath={{ name: t.AR0B(voiceSubscriptionsMsg), path: paths.PS_MOBILE_SUBSCRIPTIONS }}
    >
      {children}
    </MobileSubscriptionLayout>
  );
};
